import { isNumber } from 'lodash';

export const MAX_INT = 2147483647;
export const MIN_INT = -2147483648;

export function is32BitNumber(n: number): boolean {
  return isNumber(n) && !isNaN(n) && n <= MAX_INT && n >= MIN_INT;
}

/**
 * Options for bandwidth formatting
 */
export interface BandwidthFormatOptions {
  /** Whether to include units (default: true) */
  includeUnits?: boolean;
  /** Whether to use short format (k/m) or long format (kbps/Mbps) (default: false for long) */
  shortFormat?: boolean;
}

/**
 * Formats a bandwidth value in bps to a human-readable string
 * @param value - The bandwidth value in bits per second
 * @param options - Formatting options
 * @returns Formatted bandwidth string
 */
export function formatBandwidth(value: number, options: BandwidthFormatOptions = {}): string {
  // Handle null, undefined, or NaN values by returning empty string
  if (value === null || value === undefined || isNaN(value)) {
    return '';
  }

  const { includeUnits = true, shortFormat = false } = options;

  if (value >= 1000000) {
    const mbps = value / 1000000;
    const formattedValue = mbps % 1 === 0 ? mbps.toString() : mbps.toFixed(1);

    if (!includeUnits) return formattedValue;
    return shortFormat ? `${formattedValue}m` : `${formattedValue} Mbps`;
  }

  if (value >= 1000) {
    const kbps = value / 1000;
    const formattedValue = kbps % 1 === 0 ? kbps.toString() : kbps.toFixed(1);

    if (!includeUnits) return formattedValue;
    return shortFormat ? `${formattedValue}k` : `${formattedValue} kbps`;
  }

  if (!includeUnits) return value.toString();
  return shortFormat ? value.toString() : `${value} bps`;
}

/**
 * Formats a bandwidth value for the MbpsPipe (short format without full units)
 * @param value - The bandwidth value in bits per second
 * @returns Formatted bandwidth string in short format
 */
export function formatBandwidthShort(value: number): string {
  if (!value) {
    return '0';
  }

  if (value < 1000000) {
    return value / 1000 + 'k';
  }

  const mbps = value / 1000000;
  return value % 1000000 === 0 ? mbps + 'm' : mbps.toString();
}

/**
 * Formats a decimal number with specified precision
 * @param value - The number to format
 * @param formatArgs - Format specification (e.g., '1.0-2' for min 1, max 2 decimal places)
 * @returns Formatted decimal string
 */
export function formatDecimal(value: number, formatArgs?: string): string {
  // Handle null, undefined, or NaN values by returning empty string
  if (value === null || value === undefined || isNaN(value)) {
    return '';
  }

  const defaultDecimals = 2;

  if (!formatArgs) {
    return value.toFixed(defaultDecimals);
  }

  // Parse formatArgs like '1.0-2' (minIntegerDigits.minFractionDigits-maxFractionDigits)
  const parts = formatArgs.split('.');
  if (parts.length !== 2) {
    return value.toFixed(defaultDecimals);
  }

  const fractionPart = parts[1];
  const fractionParts = fractionPart.split('-');

  if (fractionParts.length === 1) {
    // Format like '1.2' - use as fixed decimal places
    const decimals = parseInt(fractionParts[0], 10);
    if (!isNaN(decimals) && decimals >= 0) {
      return value.toFixed(decimals);
    }
  } else if (fractionParts.length === 2) {
    // Format like '1.0-2' - min and max fraction digits
    const minDecimals = parseInt(fractionParts[0], 10);
    const maxDecimals = parseInt(fractionParts[1], 10);

    if (!isNaN(minDecimals) && !isNaN(maxDecimals) && minDecimals >= 0 && maxDecimals >= minDecimals) {
      // Use toFixed with maxDecimals, then remove trailing zeros if needed
      let result = value.toFixed(maxDecimals);

      // Remove trailing zeros, but keep at least minDecimals
      if (minDecimals < maxDecimals) {
        const decimalIndex = result.indexOf('.');
        if (decimalIndex !== -1) {
          let endIndex = result.length - 1;
          while (endIndex > decimalIndex + minDecimals && result[endIndex] === '0') {
            endIndex--;
          }
          result = result.substring(0, endIndex + 1);
        }
      }

      return result;
    }
  }

  // Fallback to default formatting for any invalid or malformed formatArgs
  return value.toFixed(defaultDecimals);
}
